import React, {useState, useEffect} from "react";
import Modal from 'react-bootstrap/Modal';
import { alertResponseMessage } from "../../actions/action-creators/response";
import { post_api_call, error_api_call, downloadBlobCreatedFile } from "../../actions/action-creators/common";
import FormDataEsg from "../../data/formData/esg/student-level";
import ValidationHelper from "../../helper/validation";
import ModalMaster from "../master";
const {
    fieldValuesExcel,
    fieldExcelLevel,
    fieldValidationExcel
} = FormDataEsg;

// Bulk ESG level assignment from Excel (user_id or email + level per row).
// Same flow and result layout as the student registration Excel upload: pick
// the file, submit, then a result modal with a failed table and a success
// table; failed rows are also downloaded as an Excel file (same three
// columns as the template plus the reason) so they can be fixed and re-uploaded.
function EsgStudentLevelExcelModal(props) {
    const [showResModal, setShowResModal] = useState(false);
    const [userData, setUserData] = useState(fieldValuesExcel);
    const [isProcess, setIsProcess] = useState(false);
    const [errorData, setErrorData] = useState([]);
    const [successRows, setSuccessRows] = useState([]);
    const [failedRows, setFailedRows] = useState([]);

    useEffect(() => {
        setUserData(fieldValuesExcel);
        setErrorData([]);
        setIsProcess(false);
        setSuccessRows([]);
        setFailedRows([]);
    }, [props.isShow]);// eslint-disable-line react-hooks/exhaustive-deps

    const updateInput = (field, value) => {
        const updated = {...userData, [field]: value};
        if(field === "excel_file"){
            updated.excel_file_data = value.files;
            updated.excel_file = value?.value;
        }
        setUserData(updated);
    };

    const _submitForm = async(e) => {
        try{
            e.preventDefault();
            setIsProcess(true);
            setErrorData([]);
            const validation = ValidationHelper.validation(userData, fieldValidationExcel).error;
            if(validation.length){
                setErrorData(validation);
                setIsProcess(false);
                return;
            }
            const apiFormData = new FormData();
            apiFormData.append('excel_file', userData.excel_file_data[0]);
            const apiData = await post_api_call('esg-student-level/import', apiFormData);
            if(apiData.status === 200){
                const failed = apiData.results.filter(r => r.result === 'failed');
                const succeeded = apiData.results.filter(r => r.result !== 'failed');
                const changed = succeeded.filter(r => r.result !== 'unchanged');
                if(failed.length === 0 && changed.length === 0){
                    // Every row already had this level: nothing changed, so no result table.
                    alertResponseMessage({alert_type: 3, message: apiData.message});
                    setIsProcess(false);
                    return;
                }
                alertResponseMessage({alert_type: failed.length ? 2 : 1, message: apiData.message});
                if(changed.length > 0){
                    props._resetListing();
                }
                if(succeeded.length > 0){
                    setSuccessRows(succeeded);
                }
                if(failed.length > 0){
                    setFailedRows(failed);
                    //automatically download data in Excel File
                    const apiExcelData = await post_api_call('sample/failed',
                                                        {data: {
                                                            data: failed.map(r => [r.user_id, r.email, r.level, r.message]),
                                                            // The export adds its own trailing "Reason" column.
                                                            headers: ['user_id', 'email', 'level'],
                                                            sheet_name: "ESG Student Levels"},
                                                        file_name: "Failed-ESG-Student-Level-Data.xlsx"
                                                    }, true, 'blob');
                    if(apiExcelData.status === 200){
                        downloadBlobCreatedFile(apiExcelData, `Failed-ESG-Student-Level-Data.xlsx`);
                    }
                }
                setShowResModal(true);
            }else if(apiData.message){
                setErrorData([apiData.message]);
            }
            setIsProcess(false);
        }catch(error){
            setIsProcess(false);
            console.error(error);
            alertResponseMessage({alert_type: 2, message: "Error updating levels! Please try after some time"});
            error_api_call({error: `modal/esg/student-level-excel|_submitForm|${error}`});
        }
    };

    return (
        <>
        {(failedRows.length > 0
            || successRows.length > 0
        )
        ?
            <Modal dialogClassName="modal-70w modal-dialog-scrollable" show={showResModal} onHide={(e) => {setShowResModal(false); props._toggleModal(e, false);}} size="md" className="mod-custom team-detail" backdrop="static">
                <Modal.Header closeButton>Excel Upload Result</Modal.Header>
                <Modal.Body>
                    {/* Failed Data */}
                    {failedRows.length > 0 && (
                        <fieldset>
                            <legend>Failed Data ({failedRows.length})</legend>
                            <div className={`row m-0 table-responsive table table-danger ${successRows.length > 0 ? 'scroll-div-30vh' : ''}`}>
                                <table className="table-bordered">
                                    <thead className="table-secondary">
                                        <tr>
                                            <th>Excel Row No.</th>
                                            <th>Reason</th>
                                            <th>User Id</th>
                                            <th>Email</th>
                                            <th>Level</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {failedRows.map((row, idx) => (
                                            <tr key={`failed_data_${idx}`}>
                                                <td>{row.row}</td>
                                                <td><b>{row.message}</b></td>
                                                <td>{row.user_id}</td>
                                                <td>{row.email}</td>
                                                <td>{row.level}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </fieldset>
                    )}
                    {/* Success Data */}
                    {successRows.length > 0 && (
                        <fieldset>
                            <legend>Updated Students ({successRows.length})</legend>
                            <div className={`row m-0 table-responsive table table-success ${failedRows.length > 0 ? 'scroll-div-30vh' : ''}`}>
                                <table className="table-bordered">
                                    <thead className="table-secondary">
                                        <tr>
                                            <th>Excel Row No.</th>
                                            <th>Status</th>
                                            <th>User Id</th>
                                            <th>Email</th>
                                            <th>Level</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {successRows.map((row, idx) => (
                                            <tr key={`success_data_${idx}`}>
                                                <td>{row.row}</td>
                                                <td><b>{row.message}</b></td>
                                                <td>{row.user_id}</td>
                                                <td>{row.email}</td>
                                                <td>{row.level}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </fieldset>
                    )}
                </Modal.Body>
                <Modal.Footer className="team-detail">
                    <button className="btn btn-secondary" onClick={(e) => {setShowResModal(false); props._toggleModal(e, false);}}>Close</button>
                </Modal.Footer>
            </Modal>
        :
            <ModalMaster
                isShow={props.isShow}
                _toggleModal={(e, state) => props._toggleModal(e, state)}
                formType={'excel_level'}
                data={userData}
                title={"ESG Student Level (Excel)"}
                _errorData={{excel_level: errorData}}
                _isProcess={{excel_level: isProcess}}
                _updateInput= {(form_type, variable_type, input_type, field, value) => updateInput(field, value)}
                _submitForm={(e, form_type) => _submitForm(e)}
                _formData={[{form_type: 'excel_level', label: 'Details', fields: [...fieldExcelLevel]}]}
                selectOptions={{}}
            />
        }
        </>
    );
}

export default EsgStudentLevelExcelModal;
